import { snapshot } from "valtio";

import {
  invalid_reset_password_code,
  mismatch_verify_code_entered,
} from "common/middleware/api.errorCodes";
import { ApiClientFactory } from "common/middleware/api.client-factory";
import {
  callApiAndHandleExpectedApiExceptions,
  ApiError,
  callApi,
} from "common/middleware/api.helpers";
import {
  ClientCredentialRequestInfo,
  RefreshAccessTokenRequest,
  VerifyAccountByUserCodeCommand,
  SendVerificationNotificationCommand,
  AccessTokenDto,
  ForgotPasswordCommand,
  ResetPasswordCommand,
} from "common/middleware/api.services";
import { mutateLoggedInUser, clearPurchaseOffersCache } from "common/middleware/hooks";
import { Logger } from "common/middleware/logger";
import store, {
  clearAll,
  updateAccessToken,
  updateMachineToken,
} from "common/middleware/store";
import { getLocale } from "common/middleware/localization";
import { requestOtp as requestOtpApi, validateOtp as validateOtpApi } from "common/middleware/api.otp";

export const logout = async () => {
  store.isSigningOut = true;
  clearAll();
  await clearPurchaseOffersCache();
  await mutateLoggedInUser(true);
};

export const sendCode = async (userId: string): Promise<boolean> => {
  Logger.debug("Sending code to user", userId);

  let accountsClient = await ApiClientFactory.getAccountsClient();
  await accountsClient.sendVerifyUserCodeEmailAccount(
    new SendVerificationNotificationCommand({
      culture: getLocale(),
      accountId: userId,
    })
  );

  Logger.debug("Code sent to user", userId);

  return true;
};

export const forgotPassword = async (userEmail: string): Promise<boolean> => {
  const result = await callApiAndHandleExpectedApiExceptions(async () => {
    let accountsClient = await ApiClientFactory.getAccountsClient(false);

    return await accountsClient.forgotPassword(
      new ForgotPasswordCommand({
        email: userEmail,
        culture: getLocale(),
        resetPasswordLinkUrl: `${window.location.origin}/reset-password`,
      })
    );
  }, []);

  if (result instanceof ApiError) {
    Logger.debug("Failed to send forgot password email");
    return false;
  }

  Logger.debug("Forgot password sent for user", userEmail);

  return true;
};

export const resetPassword = async (
  email: string,
  code: string,
  newPassword: string
): Promise<{ isSuccess: boolean, isInvalidCode?: boolean }> => {
  const result = await callApiAndHandleExpectedApiExceptions(async () => {
    let accountsClient = await ApiClientFactory.getAccountsClient(false);
    return await accountsClient.resetPassword(
      new ResetPasswordCommand({
        email: email,
        code: code,
        newPassword: newPassword,
      })
    );
  }, [invalid_reset_password_code]);

  if (result instanceof ApiError) {
    Logger.debug("API error occured", result.errorCode);

    return {
      isSuccess: false,
      isInvalidCode: true
    };
  }

  return { isSuccess: true };
};

export const verify = async (code: string): Promise<boolean> => {
  Logger.debug("Verifying token with code", code);

  const result = await callApiAndHandleExpectedApiExceptions(async () => {
    let accountsClient = await ApiClientFactory.getAccountsClient();
    return await accountsClient.verifyAccountByUserCode(
      VerifyAccountByUserCodeCommand.fromJS({
        userCode: code,
      })
    );
  }, [mismatch_verify_code_entered]);

  if (result instanceof ApiError) {
    Logger.debug("API error occured", result.errorCode);
    return false;
  }

  if (result instanceof AccessTokenDto) {
    updateAccessToken(result);

    // trigger a refresh on getLoggedInUser
    mutateLoggedInUser();

    return true;
  }

  return false;
};

export const getLoggedInUser = async () => {
  let accountClient = await ApiClientFactory.getAccountsClient();
  return await accountClient.getLoggedInUserAccount();
};

export const getMachineAccessToken = async (): Promise<string> => {
  Logger.debug("Getting machine access token");

  let authClient = await ApiClientFactory.getAuthClient(false);
  let accessToken = await authClient.auth(
    new ClientCredentialRequestInfo({
      clientId: process.env.NEXT_PUBLIC_CLIENTID,
      secretKey: process.env.NEXT_PUBLIC_SECRETKEY,
    })
  );
  if (!accessToken || !accessToken.token) {
    alert("Failed to get auth token");
    throw new Error("Failed to get auth token");
  }

  Logger.debug("Saving machine access token");
  // Need to use assign, if we do store.accessToken = accessToken then the subscribers won't get triggered.
  updateMachineToken(accessToken);

  return accessToken.token;
};

export const refreshUserAccessToken = async (): Promise<string | undefined> => {
  try {
    Logger.debug("Attempting to refresh access token");

    let authClient = await ApiClientFactory.getAuthClient(false);

    const snap = snapshot(store);

    if (!snap.accessToken?.refreshToken) {
      await logout();
      store.isSigningOut = false;
      return;
    }

    const { data: accessToken, error } = await callApi(authClient.refresh(new RefreshAccessTokenRequest({
      refreshToken: snap.accessToken.refreshToken,
    })));

    if (error) {
      await logout();
      store.isSigningOut = false;
      return;
    }

    Logger.debug("Access token refreshed, saving it");

    Object.assign(store.accessToken, accessToken);

    return accessToken.token;
  } catch (error) {
    await logout();
    store.isSigningOut = false;
  }
};

export const requestOtp = async (
  email: string,
  redirectUrl?: string
): Promise<{
  isSuccess: boolean;
  isUserNotFound?: boolean;
  errorCode?: string;
}> => {
  Logger.debug("Requesting OTP for email", email);
  return await requestOtpApi(email, redirectUrl);
};

export const validateOtp = async (
  email: string,
  otpCode: string
): Promise<{
  isSuccess: boolean;
  isInvalidCode?: boolean;
  token?: string;
  refreshToken?: string;
  isExpired?: boolean;
}> => {
  Logger.debug("Validating OTP for email", email);
  return await validateOtpApi(email, otpCode);
};
